import React from 'react'
import Modal from '../Modal'
import { CloseIcon } from './icons'

/**
 * The house dialog.
 *
 * One shape for every question these screens ask: a navy panel, the title in
 * Sora on the left, a line of plain prose under it, whatever the question needs
 * in the middle, and the buttons bottom-right with the safe one first.
 *
 * The `icon` chip is for destructive questions only. Every dialog carrying one
 * makes it decoration; a delete dialog without one looks like a form.
 *
 * Nothing here is centred. Centred text sets a different measure on every line,
 * which is why the old light dialogs needed `max-w-xs` to stay readable and
 * still broke long repository names mid-word.
 */
const ModalDialog = ({
  open,
  onClose,
  title,
  blurb,
  icon,
  width = '26rem',
  children,
  footer,
  closeButton = false,
  closeOnClickOutside = true,
}: {
  open: boolean,
  onClose: () => void,
  title: React.ReactNode,
  /** One line. If it needs two sentences it belongs in the body. */
  blurb?: React.ReactNode,
  /** Destructive dialogs only. */
  icon?: React.ReactNode,
  width?: string,
  /** Fields, lists, whatever the question needs. Optional. */
  children?: React.ReactNode,
  /** The buttons, in reading order: the way out first. */
  footer?: React.ReactNode,
  /** An × in the corner, for large views where Close is far away. */
  closeButton?: boolean,
  /** False when a stray click must not throw away work in progress. */
  closeOnClickOutside?: boolean,
}) => (
  <Modal open={open} onClose={onClose} theme="CONTAINER" showCloseIcon={false} minWidth={width} closeOnClickOutside={closeOnClickOutside}>
    <div className="flex flex-col gap-4 py-2">
      {icon ? (
        <span className="flex-none w-11 h-11 rounded-surface bg-state-err/10 flex items-center justify-center">
          {icon}
        </span>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-4">
          <h2 className="m-0 font-sora text-ds-20 font-semibold tracking-[-0.02em] text-white">
            {title}
          </h2>
          {closeButton ? (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="b4-focus flex-none -mr-1 p-1 rounded-control text-navy-200 hover:text-white hover:bg-navy-600"
            >
              <CloseIcon size={18} />
            </button>
          ) : null}
        </div>
        {blurb ? (
          <p className="m-0 text-ds-135 leading-[1.55] text-navy-200">{blurb}</p>
        ) : null}
      </div>

      {children}

      {footer ? (
        <div className="flex justify-end gap-2 mt-1">{footer}</div>
      ) : null}
    </div>
  </Modal>
)

export default ModalDialog
